// SPDX-License-Identifier: AGPL-3.0-or-later
import type {KeyCombo} from '@app/features/input/state/Keybind';
import {SHIFT_KEY_SYMBOL} from '@app/features/input/utils/KeyboardUtils';

const isMac = () => /Mac|iPod|iPhone|iPad/.test(navigator.platform);
const CONTROL_KEY_SYMBOL = '⌃';
export function formatMouseButton(button: number): string {
	switch (button) {
		case 0:
			return 'Mouse Left';
		case 1:
			return 'Mouse Middle';
		case 2:
			return 'Mouse Right';
		case 3:
			return 'Mouse Back';
		case 4:
			return 'Mouse Forward';
		default:
			return `Mouse ${button}`;
	}
}
export function formatGamepadButton(button: number): string {
	const map: Record<number, string> = {
		0: 'A / Cross',
		1: 'B / Circle',
		2: 'X / Square',
		3: 'Y / Triangle',
		4: 'Left Bumper',
		5: 'Right Bumper',
		6: 'Left Trigger',
		7: 'Right Trigger',
		8: 'Back / Select',
		9: 'Start / Options',
		10: 'Left Stick Click',
		11: 'Right Stick Click',
		12: 'D-Pad Up',
		13: 'D-Pad Down',
		14: 'D-Pad Left',
		15: 'D-Pad Right',
		16: 'Guide',
	};
	const label = map[button];
	return `Gamepad ${label ?? `Button ${button}`}`;
}
function formatBothSidesModifier(combo: KeyCombo): string | null {
	let label: string | null = null;
	if (combo.shift) label = isMac() ? SHIFT_KEY_SYMBOL : 'Shift';
	else if (combo.ctrl) label = isMac() ? CONTROL_KEY_SYMBOL : 'Ctrl';
	else if (combo.ctrlOrMeta) label = isMac() ? '⌘' : 'Ctrl';
	else if (combo.alt) label = isMac() ? '⌥' : 'Alt';
	else if (combo.meta) label = isMac() ? '⌘' : 'Win';
	if (!label) return null;
	return `Left ${label} + Right ${label}`;
}
export function formatKeyCombo(combo: KeyCombo): string {
	const parts: Array<string> = [];
	if (combo.modifierOnly && combo.bothSides) {
		const formatted = formatBothSidesModifier(combo);
		if (formatted) return formatted;
	}
	if (combo.ctrl) {
		parts.push(isMac() ? CONTROL_KEY_SYMBOL : 'Ctrl');
	} else if (combo.ctrlOrMeta) {
		parts.push(isMac() ? '⌘' : 'Ctrl');
	}
	if (combo.meta && !combo.ctrlOrMeta) {
		parts.push(isMac() ? '⌘' : 'Win');
	}
	if (combo.shift) {
		const shiftLabel = isMac() ? SHIFT_KEY_SYMBOL : 'Shift';
		parts.push(shiftLabel);
	}
	if (combo.alt) parts.push(isMac() ? '⌥' : 'Alt');
	if (combo.mouseButton !== undefined && combo.mouseButton !== null) {
		parts.push(formatMouseButton(combo.mouseButton));
		return parts.join(' + ');
	}
	if (combo.gamepadButton !== undefined && combo.gamepadButton !== null) {
		parts.push(formatGamepadButton(combo.gamepadButton));
		return parts.join(' + ');
	}
	const rawKey = combo.code && /^Numpad/.test(combo.code) ? combo.code : combo.key || combo.code || '';
	if (combo.modifierOnly && (rawKey === 'Control' || rawKey === 'Meta' || rawKey === 'Shift' || rawKey === 'Alt')) {
		return parts.join(' + ');
	}
	const primarySymbol = formatPrimaryKeySymbol(rawKey);
	parts.push(primarySymbol);
	return parts.join(' + ');
}
function formatPrimaryKeySymbol(rawKey: string): string {
	if (rawKey === ' ' || rawKey === 'Spacebar') return 'SPACEBAR';
	if (rawKey === 'ArrowUp') return '▲';
	if (rawKey === 'ArrowDown') return '▼';
	if (rawKey === 'ArrowLeft') return '◀';
	if (rawKey === 'ArrowRight') return '▶';
	if (rawKey === 'PageUp') return 'PAGE UP';
	if (rawKey === 'PageDown') return 'PAGE DOWN';
	if (rawKey === 'Escape') return 'ESC';
	if (rawKey === 'Pause') return 'BREAK';
	if (rawKey === 'Backspace') return '⌫';
	if (rawKey === 'Enter' || rawKey === 'Return') return isMac() ? '⏎' : 'Enter';
	if (rawKey === 'Tab') return 'Tab';
	if (rawKey === 'Backquote' || rawKey === '`') return '`';
	if (rawKey === '[' || rawKey === ']') return rawKey;
	if (/^Key[A-Z]$/.test(rawKey)) {
		return rawKey.slice(3);
	}
	if (/^Digit[0-9]$/.test(rawKey)) {
		return rawKey.slice(5);
	}
	if (/^Numpad[0-9]$/.test(rawKey)) {
		return `Numpad ${rawKey.slice(6)}`;
	}
	if (rawKey === 'NumpadDecimal') return 'Numpad .';
	if (rawKey === 'NumpadAdd') return 'Numpad +';
	if (rawKey === 'NumpadSubtract') return 'Numpad -';
	if (rawKey === 'NumpadMultiply') return 'Numpad *';
	if (rawKey === 'NumpadDivide') return 'Numpad /';
	if (rawKey === 'NumpadEnter') return isMac() ? 'Numpad ⏎' : 'Numpad Enter';
	if (rawKey.length === 1) {
		return rawKey.toUpperCase();
	}
	return rawKey;
}
